import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { EmptyPage } from '~/eds/src/components/EmptyPage/EmptyPage';
import { Tabs } from '~/eds/src/components/Tabs/Tabs';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';

import { AttemptModal } from './Modals/AttemptModal';
import { EndpointModal } from './Modals/EndpointModal';
import { AttemptDetails } from './Panels/AttemptDetails';
import { EndpointDetails } from './Panels/EndpointDetails';
import { Attempts } from './Tabs/Attempts';
import { Endpoints } from './Tabs/Endpoints';
import { TabType } from './types';

export const Webhooks = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { getSearchParams, setSearchParams } = useRouting();
  const searchParam = getSearchParams();
  const enableNewIntegrationPage = useFlag(FlagType.NewIntegrationsPage);

  const {
    data = { results: [], total: 0 },
    isLoading: isLoadingEndpoints,
    error: isErrorEndpoints,
  } = api.endpoints.getEndpoints.useQuery({});

  useEffect(() => {
    if (data) {
      dispatch(slices.webhooks.actions.setAllEndpoints(data.results));
    }
  }, [data]);

  //TODO: This useEffect is a temporary fix to set the endpoint tab as default for deprecated integrations page
  useEffect(() => {
    if (!searchParam.secondaryTab) {
      setSearchParams({ secondaryTab: 'endpoints' });
    }
  }, [searchParam]);

  return (
    <>
      <ContentContainer
        loadingContent={{ isLoading: isLoadingEndpoints }}
        placeholderContent={
          isErrorEndpoints
            ? {
                message: intl.formatMessage({
                  id: 'Webhooks.errorLoadingMessage',
                  defaultMessage: 'Error loading webhooks, please try again',
                  description: 'Error message when webhooks fail to load',
                }),
                image: 'error-page-error',
              }
            : undefined
        }
      >
        {data.total > 0 ? (
          <Tabs<TabType>
            actions={
              searchParam.secondaryTab === 'endpoints' &&
              !enableNewIntegrationPage
                ? [
                    {
                      text: intl.formatMessage({
                        id: 'Webhooks.addEndpointButton',
                        defaultMessage: 'Add Endpoint',
                        description: 'Button text for adding new endpoint',
                      }),
                      level: 'primary',
                      onClick: () => {
                        dispatch(
                          slices.webhooks.actions.setActiveEndpointType('add'),
                        );
                      },
                    },
                  ]
                : []
            }
            tabs={[
              {
                label: intl.formatMessage({
                  id: 'Webhooks.endpointsTabLabel',
                  defaultMessage: 'Endpoints',
                  description: 'Label for endpoints tab',
                }),
                value: 'endpoints',
                panel: <Endpoints />,
              },
              {
                label: intl.formatMessage({
                  id: 'Webhooks.attemptsTabLabel',
                  defaultMessage: 'Attempts',
                  description: 'Label for attempts tab',
                }),
                value: 'attempts',
                panel: <Attempts />,
              },
            ]}
            selectedTab={searchParam.secondaryTab}
            onSelectTab={(tab) => {
              if (tab === 'endpoints') {
                dispatch(slices.webhooks.actions.resetFilters());
                dispatch(slices.webhooks.actions.resetAttemptConfiguration());
              } else if (tab === 'attempts') {
                dispatch(slices.webhooks.actions.resetEndpointConfiguration());
              }
              setSearchParams({ secondaryTab: tab });
            }}
          />
        ) : (
          <EmptyPage
            preset="no-webhooks"
            resolveAction={{
              level: 'primary',
              onClick: () =>
                dispatch(slices.webhooks.actions.setActiveEndpointType('add')),
              text: intl.formatMessage({
                id: 'Webhooks.addEndpointEmpty',
                defaultMessage: 'Add Endpoint',
                description: 'Button text for adding endpoint on empty page',
              }),
            }}
          />
        )}
      </ContentContainer>
      <EndpointDetails />
      <AttemptDetails />
      <EndpointModal />
      <AttemptModal />
    </>
  );
};
